import { FC, useEffect, useState } from "react"
import { ChallengeSectionProps } from "../../pages/Challenge"
import {
  SubscriptionsService,
  ListUsersSubscriptionsResponseItem,
  ListTeamsSubscriptionsResponseItem,
  TasksService
} from "../../sdk"
import useError from "../../hooks/useError"
import Table from "../Table"
import PageLoader from "../PageLoader"
import { useChallenge } from "../../stores/challenge"
import Message from "../Message"
import ChallengeSubscription from "./ChallengeSubscription"
import Button from "../Button"
import classNames from "classnames"
import useDebounce from "../../hooks/useDebounce"
import Input from "../Input"
import Icon from "../Icon"
import { FiltersAndSortersType, PropertyAttributes } from "../../types/sorters-filters"
import { sortersMap, sortersValuesMap } from "../../constants/sorters"
import { Link } from "react-router-dom"
import Select from "../Select"

const ChallengeSubscriptions: FC<ChallengeSectionProps> = ({ challenge }) => {
  const { handleError } = useError()

  const {
    subscriptions: challengeSubscriptions,
    setSubscriptions: setChallengeSubscriptions,
    subscriptionsTableVisible,
    setSubscriptionsTableVisible,
    tasks,
    setTasks,
    setCurrentOpenSubscription
  } = useChallenge()

  const [loading, setLoading] = useState<boolean>(!tasks || !challengeSubscriptions)
  const [updating, setUpdating] = useState<boolean>(false)
  const [sorters, setSorters] = useState<FiltersAndSortersType["properties"] | null>(null)
  const [currentSorter, setCurrentSorter] = useState<{sorter: string | null, attributes: PropertyAttributes} | null>(null)
  const [currentSorterValue, setCurrentSorterValue] = useState<string | null>(null)

  const [searchBarValue, setSearchBarValue] = useState<string>("")
  const debouncedValue = useDebounce(searchBarValue, 500)

  const getTasks = async(): Promise<void> => {
    try {
      const { data } = await TasksService.listChallengeTasks({
        handler: {
          challengeId: challenge.id
        },
        paginator: {
          offset: 0,
          limit: 100
        }
      })
      setTasks(data)
    } catch (err) {
      handleError(err)
    }
  }

  const getSubscriptions = async(): Promise<void> => {
    if (!loading) {
      setUpdating(true)
    }
    try {
      const { data: subscriptions } = await SubscriptionsService[challenge.individual ? "listUsersSubscriptions" : "listTeamsSubscriptions"]({
        paginator: { offset: 0, limit: 100 },
        filters: { challengeId: challenge.id },
        search: debouncedValue === "" ? undefined : debouncedValue,
        sorters: currentSorter && { [currentSorter?.sorter!]: currentSorterValue || currentSorter.attributes.enum[0] } || {}
      })
      setChallengeSubscriptions(subscriptions)
    } catch (err) {
      handleError(err)

    }
    setUpdating(false)
  }

  useEffect(() => {
    void (async(): Promise<void> => {
      try {
        const { sorters } = await SubscriptionsService[challenge.individual ? "listUsersSubscriptions" : "listTeamsSubscriptions"]({
          paginator: { offset: 0, limit: 0 },
          getSorters: true,
          getFilters: true
        })

        setSorters((sorters as FiltersAndSortersType).properties)
        setCurrentSorter({ sorter: Object.entries((sorters as FiltersAndSortersType)?.properties)[0][0], attributes: Object.entries((sorters as FiltersAndSortersType)?.properties)[0][1] })
      } catch (err) {
        handleError(err)
      }
    })()
  }, [])

  useEffect(() => {
    void (async(): Promise<void> => {
      if (!tasks) {
        await getTasks()
      }
      await getSubscriptions()
      setLoading(false)
    })()
  }, [debouncedValue, currentSorter, currentSorterValue])

  return (
    loading ?
      <PageLoader className="relative mt-16" />
      :
      challengeSubscriptions && challengeSubscriptions.length > 0 ?
        subscriptionsTableVisible ?
          <>
            <div className="flex items-center gap-4 mb-8">
              <h2 className="text-2xl font-semibold">Utenti</h2>

              <Button disabled={updating} onClick={(): void => {
                void getSubscriptions()
              }}>Aggiorna</Button>
            </div>

            <div className="users-list__sorters-wrapper">
              {sorters &&
                <>
                  <Select
                    className="w-full md:w-[200px]"
                    label="Ordina per"
                    defaultValue={currentSorter && { label: sortersMap[currentSorter!.sorter!], value: currentSorter.attributes?.enum[0] }}
                    options={Object.keys(sorters).map(key => {
                      return {
                        label: sortersMap[key],
                        value: key
                      }
                    })}
                    onChange={(value): void => {
                      setCurrentSorter({ sorter: (value as { value: string }).value, attributes: sorters[(value as { value: string }).value] })
                    }}
                  />

                  <Select
                    className="w-full md:w-[200px]"
                    defaultValue={currentSorterValue && { label: sortersValuesMap[currentSorterValue], value: currentSorterValue }}
                    options={currentSorter?.attributes?.enum?.map(key => {
                      return {
                        label: sortersValuesMap[key],
                        value: key
                      }
                    })}
                    onChange={(value): void => {
                      setCurrentSorterValue((value as { value: string }).value)
                    }}
                  />
                </>
              }
            </div>

            <div className="mb-6 w-2/4">
              <Input
                value={searchBarValue}
                onChange={setSearchBarValue}
                placeholder="Cerca..."
                suffix={
                  searchBarValue !== "" &&
                <span
                  onClick={(): void => {
                    setSearchBarValue("")
                  }}
                  className="hover:cursor-pointer hover:text-gray-500 w-8 h-8 flex justify-center items-center"
                >
                  <Icon
                    name="x-close"
                    size={20}
                  />
                </span>
                }
              />
            </div>

            <Table
              className={classNames("users-list__table", updating && "opacity-50 pointer-events-none")}

              headings={
                challenge.individual
                  ?
                  ["ID", "Username", "Email", "Cognome", "Nome", "", ""]
                  :
                  ["ID", "Nome", "Membri", "", ""]
              }
              fixedColumns={["ID", "", ""]}
              rows={
                challenge.individual
                  ?
                  (challengeSubscriptions as ListUsersSubscriptionsResponseItem[] || []).map(({
                    user,
                    challengeId
                  }) => {
                    const {
                      id: userId,
                      firstname,
                      lastname,
                      email,
                      username
                    } = user

                    return {
                      id: "",
                      children: [
                        <span>{userId}</span>,
                        <span>{username}</span>,
                        <span>{email}</span>,
                        <span>{lastname}</span>,
                        <span>{firstname}</span>,
                        <span
                          className="underline text-indigo-700 hover:cursor-pointer"
                          onClick={(): void => {
                            setSubscriptionsTableVisible(!subscriptionsTableVisible)
                            setCurrentOpenSubscription({ user, challengeId })
                          }}
                        >
                          Submissions
                        </span>,
                        <Link to={`/user/${userId}`}>
                          <span className="underline text-indigo-700 hover:cursor-pointer ml-6">Visualizza</span>
                        </Link>
                      ]
                    }
                  })
                  :
                  (challengeSubscriptions as ListTeamsSubscriptionsResponseItem[] || []).map(({
                    users,
                    team,
                    challengeId
                  }) => {
                    const {
                      id: teamId,
                      name: teamName
                    } = team
                    return {
                      id: "",
                      children: [
                        <span>{teamId}</span>,
                        <span>{teamName}</span>,
                        <span>{users.length}</span>,
                        <span
                          className="underline text-indigo-700 hover:cursor-pointer"
                          onClick={(): void => {
                            setSubscriptionsTableVisible(!subscriptionsTableVisible)
                            setCurrentOpenSubscription({ team, users, challengeId })
                          }}
                        >
                          Submissions
                        </span>
                      ]
                    }
                  })
              }
            />
          </>
          :
          <ChallengeSubscription />
        :
        debouncedValue === "" ?
          <Message title="Nessun utente iscritto" icon="user-off">
            <Button onClick={(): void => {
              void getSubscriptions()
            }}>Ricarica</Button>
          </Message>
          :
          <Message title="La ricerca non ha dato alcun risultato" icon="user-off">
            <Button onClick={(): void => {
              setSearchBarValue("")
              setLoading(true)
              setTimeout(() => {
                setLoading(false)
              }, 600)
            }}>Indietro</Button>
          </Message>
  )
}

export default ChallengeSubscriptions
